import { useEffect, useRef, useState } from 'react';
import { diagnosisCodeFitnessRouteDiagnosisCodesFitnessPost } from '../services/api/sdk.gen';
import type {
  DiagnosisCodeInvalidReason,
  DiagnosisSuccessor,
  PrimaryDiagnosisFitness,
} from '../services/api/types.gen';
import { isPdgmRelevant } from '../utils/primaryDiagnosisFitness';

interface FitnessScope {
  episodeId?: string;
  visitId?: string;
  /**
   * The record's date (YYYY-MM-DD): a visit date, or the Through date of the
   * claim being edited. Validity is judged against the ICD-10-CM release in
   * force that day. Omitted means today's release.
   */
  asOf?: string;
  /**
   * The From date (YYYY-MM-DD) of the claim period being edited, with `asOf`
   * its Through date. It picks the CMS grouper version whose tables judge
   * principal fitness, span-claim predecessors included. Requires `asOf`.
   */
  claimFromDate?: string;
}

/**
 * Principal-diagnosis fitness for codes this app did not hand the caller.
 *
 * The claim worksheet stores bare code strings and the coding page reads its
 * diagnoses from RogerDoc, so neither can carry fitness on the rows themselves.
 * Both ask the EMR — the same `hipps_icd10_code` table the HIPPS calculator and
 * the claim pipeline read — rather than growing a second copy of the rule.
 *
 * Defaults assume PDGM until the episode says otherwise, so a slow or failed
 * lookup errs toward warning rather than toward silence.
 */
export function usePrimaryDiagnosisFitness(
  codes: string[],
  scope: FitnessScope = {}
) {
  const [fitnessByCode, setFitnessByCode] = useState<
    Record<string, PrimaryDiagnosisFitness>
  >({});
  // Codes a claim cannot report in any seat (deleted, or a non-billable
  // header). Valid codes are absent. Unlike fitness, this covers secondaries.
  const [invalidByCode, setInvalidByCode] = useState<
    Record<string, DiagnosisCodeInvalidReason>
  >({});
  // Replacements for invalid codes, from the CMS conversion table. An empty
  // list means "no direct conversion"; a code without an entry has no
  // suggestion to make.
  const [successorsByCode, setSuccessorsByCode] = useState<
    Record<string, DiagnosisSuccessor[]>
  >({});
  // On a claim period that spans a grouper version boundary: reported code ->
  // the predecessor Medicare groups it as, whose fitness is reported for it.
  const [spanPredecessorByCode, setSpanPredecessorByCode] = useState<
    Record<string, string>
  >({});
  const [payerContext, setPayerContext] = useState({
    groupsViaPdgm: true,
    payerKnown: false,
  });
  // "We have not asked yet" and "we asked and got no answer" are different
  // facts, and callers gate on the difference: the first is a reason to wait,
  // the second is a reason to warn. Without this flag they are both an absent
  // map entry, so a click landing before the round trip returned drew the
  // could-not-be-checked confirm over a perfectly groupable code.
  const [fitnessPending, setFitnessPending] = useState(false);
  // The last lookup was refused or failed — e.g. no code set is loaded for
  // `asOf`. Callers say the codes could not be checked, since the maps above
  // then hold an older answer (or none) that must not read as "all valid".
  const [lookupFailed, setLookupFailed] = useState(false);
  // The dates the maps above were judged for (`asOf|claimFromDate`). A
  // verdict is only true for the code set and grouper of its own dates, so
  // once the dates change the old verdicts must not stay on screen — not
  // while the new lookup is in flight, and not after it fails.
  const answeredDatesKey = useRef<string | null>(null);

  // Codes are a new array each render; key on the content so a re-render with
  // the same codes does not refetch. Sorted, because fitness is per-code and
  // order-independent — keying on the raw order made every drag-reorder refetch
  // an answer that had not changed.
  const codesKey = [...codes].sort().join(',');
  const { episodeId, visitId, asOf, claimFromDate } = scope;

  useEffect(() => {
    const requested = codesKey ? codesKey.split(',') : [];
    const datesKey = `${asOf || ''}|${claimFromDate || ''}`;
    // Still ask when there are no codes but a scope: the caller needs the payer
    // context to decide how loudly to warn about the code it is *about* to
    // seat. Skipping the round trip here left the claim worksheet permanently
    // on the optimistic default, so it full-warned known commercial payers.
    if (requested.length === 0 && !episodeId && !visitId) {
      setFitnessByCode({});
      setInvalidByCode({});
      setSuccessorsByCode({});
      setSpanPredecessorByCode({});
      setFitnessPending(false);
      return;
    }

    // Another date (the worksheet switched claims): drop the old date's
    // verdicts now, or its successor chips stay clickable until the new
    // answer lands. Same-date refetches keep them, so editing codes does not
    // flash every warning off.
    if (answeredDatesKey.current !== datesKey) {
      setFitnessByCode({});
      setInvalidByCode({});
      setSuccessorsByCode({});
      setSpanPredecessorByCode({});
    }

    let cancelled = false;
    setFitnessPending(true);
    (async () => {
      const response = await diagnosisCodeFitnessRouteDiagnosisCodesFitnessPost(
        {
          body: {
            codes: requested,
            episode_id: episodeId ?? null,
            visit_id: visitId ?? null,
            // An empty date is no date: '' would be a 422, not today's release.
            as_of: asOf || null,
            claim_from_date: claimFromDate || null,
          },
        }
      );
      if (cancelled) return;
      if (response.error || !response.data) {
        // The generated client does not throw. A failed lookup for the same
        // date leaves the previous answer in place rather than silently
        // clearing every warning; another date's answer was already dropped
        // above. Pending still clears: a failure is an answer for gating
        // purposes — the one that means warn — and leaving it set would make
        // callers wait for a response that is never coming.
        console.error('Failed to load diagnosis fitness:', response.error);
        setFitnessPending(false);
        setLookupFailed(true);
        return;
      }
      answeredDatesKey.current = datesKey;
      setFitnessPending(false);
      setLookupFailed(false);
      setFitnessByCode(response.data.fitness_by_code);
      setInvalidByCode(response.data.invalid_by_code ?? {});
      setSuccessorsByCode(response.data.successors_by_code ?? {});
      setSpanPredecessorByCode(response.data.span_predecessor_by_code ?? {});
      setPayerContext({
        // Both default false server-side; absent means "no payer resolved",
        // which isPdgmRelevant reads as warn-at-full-strength.
        groupsViaPdgm: response.data.groups_via_pdgm ?? false,
        payerKnown: response.data.payer_known ?? false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [codesKey, episodeId, visitId, asOf, claimFromDate]);

  return {
    fitnessByCode,
    invalidByCode,
    successorsByCode,
    spanPredecessorByCode,
    fitnessPending,
    lookupFailed,
    ...payerContext,
    pdgmRelevant: isPdgmRelevant(payerContext),
  };
}
